import React from 'react';
import { useTranslation } from 'react-i18next';
import { Megaphone } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'src/components/ui/button';
import { RequestAttentionAction } from 'src/pages/RequestPage/RequestActionComponents/RequestAttentionAction';
import { useDecoratedRequestContext } from 'src/features/requests/use-decorated-request-context';
import { useDecoratedRequestOutcomeContext } from 'src/features/requests/request-outcome/use-decorated-request-outcome-context';
import { useRequestOutcomesQueryMutationDecorator } from 'src/features/requests/request-outcome/use-request-outcomes-query-mutation-decorator';
import { deliverableOutcomesFilters } from 'src/features/requests/request-outcome/filter-queries';
import { authQueries } from 'src/api/queries';

const RequestOutcomeAttentionButton: React.FC = () => {
  const { t } = useTranslation();
  const client = useQueryClient();
  const request = useDecoratedRequestContext();
  const outcome = useDecoratedRequestOutcomeContext();
  const deliverableOutcomesActions = useRequestOutcomesQueryMutationDecorator(
    request.id,
    deliverableOutcomesFilters,
  );

  if (!outcome.canRequestAttention) {
    return null;
  }

  const label = outcome.needsAttention
    ? (t('models/request:state.requested_attention_at', {
        defaultValue: 'Needs attention',
      }) as string)
    : (t('actions:request_attention') as string);

  return (
    <RequestAttentionAction
      form={{
        requestId: outcome.id,
        initialValues: {
          attention_text: outcome.attentionText,
        },
        onSubmit: async ({ attention_text }) => {
          const requestedAt = new Date().toISOString();
          deliverableOutcomesActions.setData((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              items: prev.items.map((item) =>
                item.id === outcome.id
                  ? { ...item, attention_text, requested_attention_at: requestedAt }
                  : item,
              ),
            };
          });
          deliverableOutcomesActions.invalidate();
          client.invalidateQueries({ queryKey: [authQueries.requests] });
        },
      }}
      as={Button}
      variant={outcome.needsAttention ? 'destructive' : 'secondary'}
      size={'iconSm'}
      className={'tw-shrink-0'}
      title={label}
      aria-label={label}
    >
      <Megaphone className={'tw-size-4'} />
    </RequestAttentionAction>
  );
};

export { RequestOutcomeAttentionButton };
